import {
    Button,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownTrigger,
    Switch,
    Tooltip,
    ButtonGroup
} from '@heroui/react';
import { Icon } from '@iconify/react';
import clsx from 'clsx';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useTheme, type ThemeMode, type ResolvedTheme } from '../hooks/useTheme';

export type ThemeVariant = 'switch' | 'dropdown' | 'circle' | 'buttons';
export type ThemeSize = 'sm' | 'md' | 'lg';

export interface ThemeSwitchProps {
    size?: ThemeSize;
    showLabel?: boolean;
    variant?: ThemeVariant;
    enableSystem?: boolean;
    onThemeChange?: (theme: ThemeMode, resolvedTheme: ResolvedTheme) => void;
    'aria-label'?: string;
    className?: string;
}

const themeIcons = {
    light: 'solar:sun-2-line-duotone',
    dark: 'solar:moon-line-duotone',
    system: 'solar:monitor-line-duotone'
} as const;

const themeLabels = {
    light: '浅色',
    dark: '深色',
    system: '跟随系统'
} as const;

// Icon size classes based on component size
const iconSizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
} as const;

export const ThemeSwitch: React.FC<ThemeSwitchProps> = React.memo(({
    size = 'sm',
    showLabel = false,
    variant = 'switch',
    enableSystem = true,
    onThemeChange,
    'aria-label': ariaLabel,
    className
}) => {
    const { theme, resolvedTheme, systemTheme, setTheme } = useTheme();
    const [mounted, setMounted] = useState(false);

    // 确保在客户端渲染后再显示组件，避免 hydration 问题
    useEffect(() => {
        setMounted(true);
    }, []);

    // 使用 useRef 来稳定 onThemeChange 引用，避免无限循环
    const onThemeChangeRef = useRef(onThemeChange);
    onThemeChangeRef.current = onThemeChange;

    // 处理主题变化回调
    useEffect(() => {
        if (mounted && onThemeChangeRef.current) {
            onThemeChangeRef.current(theme, resolvedTheme);
        }
    }, [theme, resolvedTheme, mounted]);

    // 处理主题切换
    const handleThemeChange = useCallback((newTheme: ThemeMode) => {
        setTheme(newTheme);
    }, [setTheme]);

    const handleSwitchChange = useCallback((checked: boolean) => {
        handleThemeChange(checked ? 'dark' : 'light');
    }, [handleThemeChange]);

    const handleDropdownSelectionChange = useCallback((keys: any) => {
        const selectedTheme = Array.from(keys)[0] as ThemeMode;
        if (selectedTheme && mounted) {
            handleThemeChange(selectedTheme);
        }
    }, [handleThemeChange, mounted]);

    // Get icon size class based on component size
    const iconSizeClass = iconSizeClasses[size];

    // 获取可用的主题选项
    const getThemeOptions = useCallback((): ThemeMode[] => {
        return enableSystem ? ['light', 'dark', 'system'] : ['light', 'dark'];
    }, [enableSystem]);

    // Circle 变体的点击处理函数
    const handleCircleClick = useCallback(() => {
        if (!mounted) return;

        const options = getThemeOptions();
        const currentIndex = options.indexOf(theme);
        const nextIndex = (currentIndex + 1) % options.length;
        const nextTheme = options[nextIndex];

        handleThemeChange(nextTheme);
    }, [mounted, theme, handleThemeChange, getThemeOptions]);

    // Buttons 变体的按钮点击处理函数
    const handleButtonPress = useCallback((option: ThemeMode) => {
        handleThemeChange(option);
    }, [handleThemeChange]);

    // Switch 变体：仅支持 light/dark 切换（不支持 system）
    const renderSwitchVariant = () => {
        const isSelected = resolvedTheme === 'dark';

        return (
            <div className={clsx("flex items-center gap-2", className)}>
                <Switch
                    size={size}
                    isSelected={isSelected}
                    onValueChange={handleSwitchChange}
                    startContent={<Icon icon={themeIcons.light} className={iconSizeClass} />}
                    endContent={<Icon icon={themeIcons.dark} className={iconSizeClass} />}
                    aria-label={ariaLabel || "切换明暗主题"}
                    isDisabled={!mounted}
                />
                {showLabel && (
                    <span className="text-sm text-foreground-600">
                        {theme === 'system' ? `${themeLabels.system} (${themeLabels[resolvedTheme]})` : themeLabels[theme]}
                    </span>
                )}
            </div>
        );
    };

    // Dropdown 变体：支持所有主题选择
    const renderDropdownVariant = () => {
        const options = getThemeOptions();

        return (
            <Dropdown>
                <DropdownTrigger>
                    <Button
                        variant="light"
                        size={size}
                        isIconOnly={!showLabel}
                        startContent={<Icon icon={themeIcons[theme]} className={iconSizeClass} />}
                        aria-label={ariaLabel || "选择主题"}
                        className={clsx("min-w-unit-8 border-small border-divider", className)}
                        isDisabled={!mounted}
                    >
                        {showLabel && (
                            theme === 'system'
                                ? `${themeLabels.system} (${themeLabels[resolvedTheme]})`
                                : themeLabels[theme]
                        )}
                    </Button>
                </DropdownTrigger>
                <DropdownMenu
                    aria-label="主题选择"
                    selectedKeys={mounted ? [theme] : []}
                    selectionMode="single"
                    onSelectionChange={handleDropdownSelectionChange}
                >
                    {options.map((option) => (
                        <DropdownItem
                            key={option}
                            startContent={<Icon icon={themeIcons[option]} className={iconSizeClass} />}
                        >
                            {option === 'system' ? `${themeLabels[option]} (${themeLabels[systemTheme]})` : themeLabels[option]}
                        </DropdownItem>
                    ))}
                </DropdownMenu>
            </Dropdown>
        );
    };

    // Circle 变体：圆形按钮，点击循环切换
    const renderCircleVariant = () => {
        const buttonContent = (
            <Button
                isIconOnly={!showLabel}
                variant="light"
                radius="full"
                size={size}
                onPress={handleCircleClick}
                aria-label={ariaLabel || `当前主题: ${themeLabels[theme]}, 点击切换`}
                className={clsx("min-w-unit-8 border-small border-divider hover:border-primary", className)}
                startContent={!showLabel ? <Icon icon={themeIcons[theme]} className={iconSizeClass} /> : undefined}
                isDisabled={!mounted}
            >
                {showLabel && (
                    <div className="flex items-center gap-2">
                        <Icon icon={themeIcons[theme]} className={iconSizeClass} />
                        <span className="text-sm">
                            {theme === 'system'
                                ? `${themeLabels.system} (${themeLabels[resolvedTheme]})`
                                : themeLabels[theme]
                            }
                        </span>
                    </div>
                )}
            </Button>
        );

        if (showLabel) {
            return buttonContent;
        }

        return (
            <Tooltip
                content={
                    theme === 'system'
                        ? `${themeLabels.system} (${themeLabels[resolvedTheme]}) - 点击切换`
                        : `${themeLabels[theme]} - 点击切换`
                }
            >
                {buttonContent}
            </Tooltip>
        );
    };

    // Buttons 变体：按钮组，显示所有选项
    const renderButtonsVariant = () => {
        const options = getThemeOptions();

        return (
            <div className={clsx("flex flex-col gap-2", className)}>
                <ButtonGroup size={size} variant="flat" className="w-full">
                    {options.map((option) => (
                        <Button
                            key={option}
                            onPress={() => handleButtonPress(option)}
                            variant={theme === option ? "solid" : "flat"}
                            color={theme === option ? "primary" : "default"}
                            startContent={<Icon icon={themeIcons[option]} className={iconSizeClass} />}
                            className="flex-1"
                            isDisabled={!mounted}
                            aria-label={`选择${themeLabels[option]}主题`}
                        >
                            {showLabel ? themeLabels[option] : null}
                        </Button>
                    ))}
                </ButtonGroup>
                {showLabel && (
                    <div className="text-xs text-center text-foreground-500 mt-1">
                        当前: {theme === 'system'
                            ? `${themeLabels.system} (${themeLabels[resolvedTheme]})`
                            : themeLabels[theme]
                        }
                    </div>
                )}
            </div>
        );
    };

    // 防止 SSR 不匹配，在客户端渲染前显示占位符
    if (!mounted) {
        return (
            <div className={clsx("opacity-0", className)} style={{ width: '32px', height: '32px' }}>
                {/* 占位符，防止布局跳动 */}
            </div>
        );
    }

    // 根据变体渲染不同的组件
    switch (variant) {
        case 'switch':
            return renderSwitchVariant();
        case 'dropdown':
            return renderDropdownVariant();
        case 'circle':
            return renderCircleVariant();
        case 'buttons':
            return renderButtonsVariant();
        default:
            return renderSwitchVariant();
    }
});

ThemeSwitch.displayName = "ThemeSwitch";